import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { ImageCroppedEvent, ImageCropperComponent, LoadedImage } from 'ngx-image-cropper';

import { tuainFormsBaseModules, tuainBaseComponents } from '@components/tuain.modules';

import { pdfPreview } from './preview';

const DEFAULT_BODY_MESSAGE = 'Haga click o arrastre su archivo aquí';
const DEFAULT_FILE_TYPES = '*'; //  'application/pdf,image/*'
const DEFAULT_MAX_FILE_SIZE = 5000000;
const ERROR_DETAILS = {
  size: 'El archivo supera el tamaño máximo permitido',
};

@Component({
  standalone: true,
  selector: 'app-modal-standard',
  imports: [...tuainFormsBaseModules, ...tuainBaseComponents],
  template: `
    <div>
      <p>{{ body }}</p>
      <p [innerHtml]="rawBody"></p>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StandardModalComponent {
  body = '';
  rawBody = '';

  setContent(params) {
    this.body = params?.body || '';
    this.rawBody = params?.rawBody || '';
  }
}

@Component({
  standalone: true,
  selector: 'app-modal-upload',
  imports: [...tuainFormsBaseModules, ...tuainBaseComponents],
  template: `
    @if (errorMessage) {
      <div class="mb-2">
        <nz-alert nzType="error" [nzMessage]="errorMessage" nzShowIcon></nz-alert>
      </div>
    }
    @if (!previewBurnedPdf) {
      <div
        ngx-dropzone
        [accept]="accept"
        [multiple]="'false'"
        [maxFileSize]="maxFileSize"
        (change)="onSelect($event)"
        [class]="modalClass"
      >
        <ngx-dropzone-label>
          <div>
            <p><i nz-icon nzType="cloud-upload"></i></p>
            <p>{{ body }}</p>
          </div>
        </ngx-dropzone-label>
        @for (f of files; track $index) {
          <ngx-dropzone-image-preview
            ngProjectAs="ngx-dropzone-preview"
            [file]="f"
            [removable]="true"
            (removed)="onRemove(f)"
          >
            <ngx-dropzone-label>{{ f.name }} ({{ f.type }})</ngx-dropzone-label>
          </ngx-dropzone-image-preview>
        }
      </div>
    }
    @if (previewBurnedPdf) {
      <div [class]="modalClass">
        <img [src]="pdfPreview" class="pdfPreview" alt="" />
      </div>
    }
  `,
  styles: [
    `
      [nz-icon] {
        font-size: 32px;
      }

      .pdfPreview {
        max-width: 140px;
        opacity: 0.8;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadModalComponent {
  callback: any = null;
  errorMessage = '';
  body = '';
  accept: string;
  multiple = false;
  maxFileSize = '';
  files: File[] = [];
  modalClass = '';
  previewBurnedPdf: any;
  pdfPreview: any;

  setContent(params) {
    this.callback = params?.callback;
    this.body = params?.body || DEFAULT_BODY_MESSAGE;
    this.accept = params?.accept || DEFAULT_FILE_TYPES;
    this.maxFileSize = params?.maxFileSize || DEFAULT_MAX_FILE_SIZE;
    this.modalClass = params?.modalClass || '';
    if (params?.maxFileSizeErr) {
      ERROR_DETAILS.size = params?.maxFileSizeErr;
    }
  }

  onSelect(event) {
    const reason = event?.rejectedFiles[0]?.reason;
    this.errorMessage = reason ? ERROR_DETAILS[reason] : '';
    this.files = [...event.addedFiles];
    if (this.files?.[0]) {
      const fileExt = this.files[0]?.name?.split('.')?.pop()?.toLowerCase() ?? '';
      this.previewBurnedPdf = fileExt === 'pdf' ? true : false;
      this.pdfPreview = pdfPreview;
    }
  }

  onRemove(event) {
    this.files.splice(this.files.indexOf(event), 1);
  }

  getFiles(callback) {
    const reader = new FileReader();
    if (this.files && this.files.length) {
      const [file] = this.files;
      reader.readAsDataURL(file);
      reader.onload = () => {
        const fileContentObj = {
          fileName: file.name,
          fileType: file.type,
          fileContent: reader.result,
        };
        callback(fileContentObj);
      };
      return;
    }
    callback(null);
  }
}

@Component({
  standalone: true,
  selector: 'app-image-upload',
  imports: [...tuainFormsBaseModules, ...tuainBaseComponents, ImageCropperComponent],
  template: `
    @if (errorMessage) {
      <div class="mb-2">
        <nz-alert nzType="error" [nzMessage]="errorMessage" nzShowIcon></nz-alert>
      </div>
    }
    <label for="attachFile">{{ body }}:</label>
    <br />
    <input type="file" id="attachFile" name="attachFile" (change)="fileChangeEvent($event)" />
    <image-cropper
      [imageChangedEvent]="imageChangedEvent"
      [maintainAspectRatio]="true"
      [aspectRatio]="aspectRatio"
      format="png"
      output="base64"
      (imageCropped)="imageCropped($event)"
      (imageLoaded)="imageLoaded($event)"
      (cropperReady)="cropperReady()"
      (loadImageFailed)="loadImageFailed()"
    ></image-cropper>
  `,
  styles: [
    `
      [nz-icon] {
        font-size: 32px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageUploadModalComponent {
  aspectRatio: number = 4 / 3;
  errorMessage = '';
  body = '';
  maxFileSize = '';
  fileName = '';
  fileType = '';
  callback: any = null;
  imageChangedEvent: any = '';
  croppedImage: any = '';

  setContent(params) {
    this.callback = params?.callback;
    this.body = params?.body || DEFAULT_BODY_MESSAGE;
    this.aspectRatio = params?.aspectRatio ?? 1;
    this.maxFileSize = params?.maxFileSize || DEFAULT_MAX_FILE_SIZE;
  }

  fileChangeEvent(event: any): void {
    const { name, size: _size, type } = event?.target.files?.[0] ?? {};
    this.fileName = name;
    this.fileType = type;
    this.imageChangedEvent = event;
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.base64;
  }

  imageLoaded(_image: LoadedImage) {
    /* show cropper */
  }
  cropperReady() {
    /* cropper ready */
  }
  loadImageFailed() {
    /* show message */
  }

  getFiles(callback) {
    callback({
      fileContent: this.croppedImage,
      fileName: this.fileName,
      fileType: this.fileType,
    });
  }
}

@Component({
  standalone: true,
  selector: 'app-modal-affiliation-resume',
  imports: [...tuainFormsBaseModules, ...tuainBaseComponents],
  template: `
    <div class="ar-modal">
      <div class="ar-modal__icon-ring">
        <span nz-icon nzType="carry-out" nzTheme="filled" class="ar-modal__icon"></span>
      </div>

      <h3 class="ar-modal__title">{{ greeting }}</h3>
      <p class="ar-modal__subtitle">Su proceso de afiliación quedó guardado en la etapa:</p>

      <div class="ar-modal__step-badge">
        <span nz-icon nzType="aim" nzTheme="outline" class="ar-modal__step-icon"></span>
        <span class="ar-modal__step-label">{{ stepLabel }}</span>
      </div>

      <div class="ar-modal__tip">
        <span nz-icon nzType="info-circle" nzTheme="outline" class="ar-modal__tip-icon"></span>
        {{ stepTip }}
      </div>
    </div>
  `,
  styles: [
    `
      .ar-modal {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        padding: 4px 0 8px;
      }

      .ar-modal__icon-ring {
        width: 72px;
        height: 72px;
        border-radius: 50%;
        background: linear-gradient(145deg, #e6f0fa, #cce0f5);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 16px;
        box-shadow: 0 4px 12px rgba(0, 51, 102, 0.15);
      }

      .ar-modal__icon {
        font-size: 38px;
        color: #003366;
      }

      .ar-modal__title {
        font-size: 20px;
        font-weight: 700;
        color: #003366;
        margin: 0 0 6px;
      }

      .ar-modal__subtitle {
        font-size: 14px;
        color: #667799;
        margin: 0 0 14px;
      }

      .ar-modal__step-badge {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        background: linear-gradient(135deg, #003366, #0055aa);
        color: #ffffff;
        border-radius: 24px;
        padding: 9px 22px;
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 18px;
        box-shadow: 0 3px 10px rgba(0, 51, 102, 0.3);
      }

      .ar-modal__step-icon {
        font-size: 16px;
      }

      .ar-modal__step-label {
        letter-spacing: 0.01em;
      }

      .ar-modal__tip {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        text-align: left;
        background: #f0f5ff;
        border-left: 3px solid #0066cc;
        border-radius: 6px;
        padding: 12px 16px;
        font-size: 13px;
        color: #334466;
        line-height: 1.55;
        max-width: 360px;
      }

      .ar-modal__tip-icon {
        font-size: 16px;
        color: #0066cc;
        flex-shrink: 0;
        margin-top: 1px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AffiliationResumeModalComponent {
  private readonly _cdr = inject(ChangeDetectorRef);

  greeting = '';
  stepLabel = '';
  stepTip = '';

  // El gateway arma el saludo según el sexo registrado; sin él se usa uno neutro.
  setContent(params: { greeting?: string; stepLabel?: string; stepTip?: string }) {
    this.greeting = params?.greeting || '¡Hola de nuevo!';
    this.stepLabel = params?.stepLabel ?? '';
    this.stepTip =
      params?.stepTip ??
      'Toda la información que ingresó está guardada. Continúe desde donde lo dejó sin repetir pasos.';
    this._cdr.markForCheck();
  }
}

/** Mensaje acordado cuando no se puede completar la firma digital (ESB-380 sin certificado). */
export const SIGNATURE_UNAVAILABLE_MESSAGE =
  'En este momento no es posible completar la firma digital. Por favor intenta nuevamente más tarde retomando la solicitud desde el home de la plataforma.';

/**
 * Firma digital no disponible: el ESB-380 no emitió el certificado de firma y, por tanto, no hay PIN
 * que pedir. Reemplaza el aviso genérico de error con un modal que explica qué pasó y que la
 * afiliación quedó guardada para retomarla desde el inicio. Mismo lenguaje visual que
 * AffiliationResumeModalComponent.
 */
@Component({
  standalone: true,
  selector: 'app-modal-signature-unavailable',
  imports: [...tuainFormsBaseModules, ...tuainBaseComponents],
  template: `
    <div class="su-modal" role="alertdialog" aria-labelledby="su-modal-title" aria-describedby="su-modal-message">
      <div class="su-modal__icon-ring">
        <span nz-icon nzType="safety-certificate" nzTheme="outline" class="su-modal__icon"></span>
      </div>

      <h3 id="su-modal-title" class="su-modal__title">Firma digital no disponible</h3>
      <p id="su-modal-message" class="su-modal__message">{{ message }}</p>

      <div class="su-modal__tip">
        <span nz-icon nzType="info-circle" nzTheme="outline" class="su-modal__tip-icon"></span>
        Tu información quedó guardada. Desde tus productos en el inicio podrás retomar la afiliación y completar la
        firma.
      </div>
    </div>
  `,
  styles: [
    `
      .su-modal {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        padding: 8px 0 8px;
      }

      .su-modal__icon-ring {
        width: 76px;
        height: 76px;
        border-radius: 50%;
        background: linear-gradient(145deg, #fff4e0, #ffe2b3);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 18px;
        box-shadow: 0 4px 14px rgba(204, 122, 0, 0.22);
      }

      .su-modal__icon {
        font-size: 38px;
        color: #c76b00;
      }

      .su-modal__title {
        font-size: 20px;
        font-weight: 700;
        color: #003366;
        margin: 0 0 10px;
      }

      .su-modal__message {
        font-size: 15px;
        line-height: 1.55;
        color: #334466;
        margin: 0 0 18px;
        max-width: 380px;
      }

      .su-modal__tip {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        text-align: left;
        background: #f0f5ff;
        border-left: 3px solid #0066cc;
        border-radius: 6px;
        padding: 12px 16px;
        font-size: 13px;
        color: #334466;
        line-height: 1.55;
        max-width: 380px;
      }

      .su-modal__tip-icon {
        font-size: 16px;
        color: #0066cc;
        flex-shrink: 0;
        margin-top: 1px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignatureUnavailableModalComponent {
  private readonly _cdr = inject(ChangeDetectorRef);

  message = SIGNATURE_UNAVAILABLE_MESSAGE;

  setContent(params: { message?: string }) {
    this.message = params?.message || SIGNATURE_UNAVAILABLE_MESSAGE;
    this._cdr.markForCheck();
  }
}
